import { useEffect, useRef, useState } from 'react';
import { MP4SelectProps, MP4VideoTrack, settingControllerKey } from '../../../Player/PlayerModels';
import { CustomOption, CustomOptionWrapper, SelectingItemMainWrapper, SettingPanelMultiSelectItem } from '../../../Player/styles';
import { PanelSettingHeader, PanelSettingLayer } from '../../../Player/styles/Panel';
import { Box } from '../../../../styles';
import {  IconQualityChange } from '../../../../icons';
import UsePlayerProps from '../../../../Store/usePlayerProps';

const formatQualityLabel = (quality: number | string) => (Number(quality) > 1080 ? 'HQ' : `${quality}`);

export default function ControlBarQualityLevelPanel({ player, handleSettingItemClick, activeSetting }: {
  player: HTMLVideoElement,
  handleSettingItemClick: (controllerKey: settingControllerKey) => void
  activeSetting: string | null
}) {
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const { sources } = playerGlobalState;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMP4Quality, setSelectedMP4Quality] = useState<number>(720);
  const lastTime = useRef<number>(0);
  // Set while a switch is still loading. Its currentTime reads 0 until then, so a second switch
  // in that window has to keep the position the first one saved.
  const restorePending = useRef<boolean>(false);

  useEffect(() => {
    if (lastTime.current <= 0) return;

    // One-shot and removed by reference: this effect re-runs on every quality switch, and the
    // old inline listener stayed attached, re-seeking on each later load.
    const restoreTime = () => {
      restorePending.current = false;
      player.currentTime = lastTime.current;
    };
    restorePending.current = true;
    player.addEventListener('loadeddata', restoreTime, { once: true });
    return () => player.removeEventListener('loadeddata', restoreTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.src]);

  useEffect(() => {
    if (!sources?.videos) return;
    const currentSrc = player.currentSrc || player.src;
    const currentVideo = (sources.videos as MP4VideoTrack[]).find((v) => v.src === currentSrc);
    const defaultQuality = sources.videos[0] as   MP4VideoTrack;
    setSelectedMP4Quality(currentVideo?.quality || defaultQuality?.quality || 720);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources?.videos]);

  const toggleMenu = (e, key) => {
    e.stopPropagation();
    handleSettingItemClick(key);
    setIsOpen(!isOpen);
  };

  const handleMP4QualityChange = (player: HTMLVideoElement, source: MP4VideoTrack) => {
    if (!restorePending.current) lastTime.current = player.currentTime;
    player.src = source?.src as string;
  };

  return (
    <>
      {!activeSetting && <SettingPanelMultiSelectItem icon={<IconQualityChange />} label="player.quality" onClick={(e) => toggleMenu(e, 'quality')} value={formatQualityLabel(selectedMP4Quality)} />}
      {activeSetting === 'quality' && <PanelSettingLayer>
        <MP4Select
          returnAction={(e) => toggleMenu(e, null)}
          options={sources?.videos as MP4VideoTrack[]}
          onChange={(quality: number, src: MP4VideoTrack) => {
            if (quality !== selectedMP4Quality) {
              setSelectedMP4Quality(Number(quality));
              handleMP4QualityChange(player, src);
            }
            setIsOpen(false);
          }}
          value={selectedMP4Quality}
        />
      </PanelSettingLayer>}
    </>
  );
}
const MP4Select = ({ value, options, onChange, returnAction }: MP4SelectProps) => {
  return (
    <Box>
      <PanelSettingHeader label={'player.selected_quality'} handleClick={(e) => returnAction(e)}  />
      <SelectingItemMainWrapper>
        {options.map((item: MP4VideoTrack) => (
          <CustomOptionWrapper key={item.id} onClick={(e: any) => {
            onChange(item.quality, item);
            returnAction(e);
          }}>
            <CustomOption selected={item.quality === value}>
              {formatQualityLabel(item.quality)}
            </CustomOption>
          </CustomOptionWrapper>
        ))}
      </SelectingItemMainWrapper>
    </Box>
  );
};
